/**
 * VersichererRankingSection — the Prozessschritt-5 body of Tab 3a (P2 Placement Slice 7). Lists the
 * ranked Versicherer tiles, lets the broker multi-select, and persists the set via «Auswahl
 * speichern» (PUT-replace). The local `pending` set is seeded from the stored selection — BOTH the
 * ranking tiles' `ausgewaehlt` flag AND the Auswahl list — and RE-SEEDED on every successful refetch
 * (the Slice-5 "refetch re-sync" lesson). A carrier that was selected but has since dropped out of
 * the ranking universe (policies soft-deleted → stats zeroed, or now ausgeschlossen) still appears
 * as an "off-ranking but selected" tile so it can be deselected — otherwise an unrelated save would
 * silently drop it (or 409 on a locked row with no tile to fix). No status transition (D4).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { CountBadge } from '../../components/ui/CountBadge';
import { dateShortDe, timeShortDe } from '../../lib/format';

/** #14B: the forced-refresh cooldown window (kept in sync with CARRIER_STATS_MIN_REFRESH_INTERVAL_MINUTES). */
const CARRIER_STATS_COOLDOWN_MS = 2 * 60_000;
import { VERSICHERER_AUSWAHL_STATUS, VERSICHERER_NAECHSTER_SCHRITT_HINWEIS, VERSICHERER_UNIVERSUM_LEER_HINWEIS, VERSICHERER_VORGABEN_OFFEN_HINWEIS } from '@brokly/shared';
import type { VersichererAuswahlEintrag, VersichererRankingEintrag } from '@brokly/shared';
import { VersichererTile } from './VersichererTile';
import { usePlacementVersichererRanking, usePlacementVersichererAuswahl, useRefreshVersichererStats, usePutVersichererAuswahl } from './usePlacementVersicherer';

/** Synthesize a ranking tile for a selected carrier that is no longer in the ranking universe. */
function offRankingTile(item: VersichererAuswahlEintrag): VersichererRankingEintrag {
  const kz = { anzahl_policen_total: item.aktive_policen_bei_auswahl, anzahl_policen_aktiv: item.aktive_policen_bei_auswahl, letzte_platzierung_am: null };
  return {
    rang: 0,
    carrier_uid: item.carrier_uid,
    name: item.name,
    logo_url: item.logo_url,
    email_submission: null,
    submission_faehig: true, // unknown off-ranking — do not nag with the e-mail warning
    eignung: item.eignung_bei_auswahl,
    eignung_gruende: [],
    sparte: { anzahl_policen_total: 0, anzahl_policen_aktiv: 0, letzte_platzierung_am: null },
    familie: kz,
    gesamt: kz,
    ausgewaehlt: true,
    auswahl_status: item.status,
    auswahl_gesperrt: item.submission_id !== null,
  };
}

function mapPutError(message: string): string {
  if (message.includes('selection_locked')) return 'Bereits angefragte Versicherer können nicht entfernt werden.';
  if (message.includes('vorgaben_nicht_erfasst')) return VERSICHERER_VORGABEN_OFFEN_HINWEIS;
  if (message.includes('placement_locked')) return 'Die Ausschreibung ist gesperrt und kann nicht mehr bearbeitet werden.';
  if (message.includes('carrier_not_in_ranking')) return 'Versicherer nicht mehr im Ranking - bitte neu laden.';
  if (message.includes('carrier_nicht_geeignet')) return 'Ein ausgewählter Versicherer ist nicht geeignet.';
  return message || 'Speichern fehlgeschlagen.';
}

export function VersichererRankingSection({ placementId, canWrite }: { placementId: string; canWrite: boolean }) {
  const rankingQuery = usePlacementVersichererRanking(placementId);
  const auswahlQuery = usePlacementVersichererAuswahl(placementId);
  const refresh = useRefreshVersichererStats(placementId);
  const put = usePutVersichererAuswahl(placementId);

  const ranking = rankingQuery.data;
  const auswahl = auswahlQuery.data;
  const [saveError, setSaveError] = useState<string | null>(null);

  // Carriers currently in the ranking universe (the tiles the broker can newly add/remove).
  const rankingUids = useMemo(
    () => new Set((ranking?.versicherer ?? []).map((v) => v.carrier_uid)),
    [ranking],
  );
  // Selected-but-off-ranking carriers: ausgewaehlt in the Auswahl list, absent from the ranking.
  const offRanking = useMemo(
    () =>
      (auswahl?.items ?? [])
        .filter((i) => i.status === VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT && !rankingUids.has(i.carrier_uid))
        .map(offRankingTile),
    [auswahl, rankingUids],
  );

  // The stored selection = ranking tiles flagged ausgewaehlt ∪ the Auswahl list's ausgewaehlt rows
  // (so an off-ranking selected carrier is still part of the set a save must preserve).
  const stored = useMemo(() => {
    const set = new Set((ranking?.versicherer ?? []).filter((v) => v.ausgewaehlt).map((v) => v.carrier_uid));
    for (const i of auswahl?.items ?? []) {
      if (i.status === VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT) set.add(i.carrier_uid);
    }
    return set;
  }, [ranking, auswahl]);
  const storedSig = [...stored].sort().join(',');

  const [pending, setPending] = useState<Set<string>>(() => new Set(stored));
  const lastSig = useRef<string | null>(null);
  // Re-seed `pending` from the stored set whenever a (re)fetch changes it — never keep stale dirt.
  useEffect(() => {
    if (lastSig.current !== storedSig) {
      lastSig.current = storedSig;
      setPending(new Set(stored));
    }
  }, [storedSig, stored]);

  // #14B cooldown self-release: cooldownActive is a render-time computation, so without a trigger the
  // refresh button would stay disabled past the 2-minute window until some unrelated re-render. A
  // single timeout to the known expiry forces one re-render the moment the cooldown elapses.
  const [, setCooldownTick] = useState(0);
  const cooldownExpiresAt = ranking?.stats.computed_at
    ? Date.parse(ranking.stats.computed_at) + CARRIER_STATS_COOLDOWN_MS
    : NaN;
  useEffect(() => {
    if (Number.isNaN(cooldownExpiresAt)) return;
    const remaining = cooldownExpiresAt - Date.now();
    if (remaining <= 0) return;
    const t = setTimeout(() => setCooldownTick((n) => n + 1), remaining + 50);
    return () => clearTimeout(t);
  }, [cooldownExpiresAt]);

  if (!ranking) {
    return <p className="p-2 text-sm text-muted-foreground">Lade Versicherer…</p>;
  }

  const auswahlErlaubt = ranking.auswahl_erlaubt;
  const pendingSig = [...pending].sort().join(',');
  const dirty = pendingSig !== storedSig;
  const sparteIsLeaf = ranking.branchencode !== ranking.familie;

  function toggle(uid: string) {
    if (!auswahlErlaubt || !canWrite) return;
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }

  async function save() {
    setSaveError(null);
    try {
      await put.mutateAsync({ carrier_uids: [...pending] });
    } catch (err) {
      setSaveError(mapPutError(err instanceof Error ? err.message : ''));
    }
  }

  const lockBanner = !auswahlErlaubt
    ? ranking.auswahl_sperrgrund === 'vorgaben_nicht_erfasst'
      ? VERSICHERER_VORGABEN_OFFEN_HINWEIS
      : 'Die Ausschreibung ist gesperrt und kann nicht mehr bearbeitet werden.'
    : null;

  // #14B cooldown: the button is disabled while a forced refresh would be skipped — the stats are
  // fresh (not stale) and were computed under 2 minutes ago. Recomputed on render + after the
  // mutation settles (which refetches the ranking, so computed_at advances).
  const computedAtMs = ranking.stats.computed_at ? Date.parse(ranking.stats.computed_at) : NaN;
  const cooldownActive =
    !ranking.stats.stale && !Number.isNaN(computedAtMs) && Date.now() - computedAtMs < CARRIER_STATS_COOLDOWN_MS;

  return (
    <section className="flex flex-col gap-3" data-testid="placement-versicherer-section">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Versicherer-Ranking</h3>
        <CountBadge count={pending.size} />
        <span className="text-xs text-muted-foreground">ausgewählt</span>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Eignung: Bestand</span>
          {canWrite && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending || cooldownActive}
              title={`Stand ${dateShortDe(ranking.stats.computed_at)}`}
              data-testid="vrs-refresh"
            >
              Kennzahlen aktualisieren{ranking.stats.stale ? ' (veraltet)' : ''}
            </Button>
          )}
        </span>
      </div>

      {refresh.data && !refresh.data.stats.refreshed && (
        <p role="note" className="text-xs text-muted-foreground" data-testid="vrs-refresh-busy">
          {refresh.data.stats.skip_grund === 'cooldown'
            ? `Kennzahlen sind aktuell (Stand ${timeShortDe(refresh.data.stats.computed_at)}). Erneute Aktualisierung ab ${timeShortDe(refresh.data.stats.naechste_aktualisierung_ab)} möglich.`
            : 'Kennzahlen werden gerade berechnet (läuft bereits) - bitte in Kürze neu laden.'}
        </p>
      )}

      {lockBanner && (
        <p role="note" className="rounded-md border border-status-warning-border bg-status-warning-bg px-3 py-2 text-sm text-status-warning" data-testid="vrs-lock-hint">
          {lockBanner}
        </p>
      )}

      {ranking.versicherer.length === 0 && offRanking.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="vrs-empty">
          {VERSICHERER_UNIVERSUM_LEER_HINWEIS}
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {ranking.versicherer.map((v) => (
            <VersichererTile
              key={v.carrier_uid}
              eintrag={v}
              checked={pending.has(v.carrier_uid)}
              disabled={!auswahlErlaubt || !canWrite || v.auswahl_gesperrt}
              onToggle={() => toggle(v.carrier_uid)}
              familieLabel={ranking.familie_label}
              sparteLabel={ranking.branchen_label}
              sparteIsLeaf={sparteIsLeaf}
            />
          ))}
        </div>
      )}

      {offRanking.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="vrs-offranking">
          <p className="text-xs text-muted-foreground">
            Ausgewählt, aber nicht mehr im Ranking (Bestand geändert) - zum Entfernen abwählen und speichern:
          </p>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {offRanking.map((v) => (
              <VersichererTile
                key={v.carrier_uid}
                eintrag={v}
                checked={pending.has(v.carrier_uid)}
                disabled={!auswahlErlaubt || !canWrite || v.auswahl_gesperrt}
                onToggle={() => toggle(v.carrier_uid)}
                familieLabel={ranking.familie_label}
                sparteLabel={ranking.branchen_label}
                sparteIsLeaf={sparteIsLeaf}
              />
            ))}
          </div>
        </div>
      )}

      {auswahlErlaubt && (
        <p className="text-xs text-muted-foreground">{VERSICHERER_NAECHSTER_SCHRITT_HINWEIS}</p>
      )}

      {dirty && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-border bg-card/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">Ungespeicherte Änderungen</span>
          <span className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPending(new Set(stored))} disabled={put.isPending} data-testid="vrs-reset">
              Zurücksetzen
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={put.isPending} data-testid="vrs-save">
              {put.isPending ? 'Wird gespeichert …' : 'Auswahl speichern'}
            </Button>
          </span>
          {saveError && (
            <p role="alert" className="w-full text-sm text-status-danger" data-testid="vrs-save-error">
              {saveError}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
